import React, { createContext, useContext, useMemo } from 'react'
import { useSnackData } from 'src/context/SnackContext'
import { get } from 'src/utils/config/lodashUtils'
import { logErrorWithContext } from 'src/utils/errorLogger'
import { useLoadingData } from './LoadingContext'

interface ErrorOptions {
  silent?: boolean
  context?: { [key: string]: any }
}

interface ErrorContentInterface {
  handleError(error: string | Error | any, options?: ErrorOptions | { [key: string]: any }): void
  asyncWrapper(fn: any, options?: { silent?: boolean }): Promise<void>
  openError: (text: string, autoHideDuration?: number) => void
  openSuccess: (text: string, autoHideDuration?: number) => void
  handleAsync(fn: () => Promise<any>, options?: { silent?: boolean }): Promise<any>
}

export const schemaError = "Failed to execute 'transaction' on 'IDBDatabase'"

const initialState = {} as ErrorContentInterface

const ErrorContext = createContext<ErrorContentInterface>(initialState)

export const useErrorData = () => useContext(ErrorContext)

const ErrorContextProvider = ({ children }: { children: React.ReactNode }) => {
  const { setSnack, openSuccess, openError } = useSnackData()
  const { setBackdropLoading } = useLoadingData()

  function handleError(error: string | Error | any, options?: ErrorOptions | { [key: string]: any }) {
    // Normalize options - support both old { context } and new { silent, context } formats
    // Default: silent=true (no snackbar), pass { silent: false } to show snackbar
    const isSilent = options && 'silent' in options ? options.silent : true
    const context = options && 'context' in options ? options.context : undefined

    // Extract error message for display
    let errorMessage: string
    if (typeof error === 'string') {
      errorMessage = error
    } else if (error instanceof Error) {
      errorMessage = error.message
    } else {
      errorMessage = get(error, 'message', 'Something went wrong')
    }

    // Show error to user (skip if silent mode)
    if (!isSilent && error && (typeof error === 'string' || typeof error === 'object')) {
      if (typeof error === 'object') {
        setSnack({ type: 'error', open: true, text: get(error, 'message', error) })
      } else {
        setSnack({ type: 'error', open: true, text: error })
      }
    }

    // Enhanced error logging with context and stack traces (always log)
    logErrorWithContext(error, {
      context,
      source: 'handleError',
      component: 'ErrorContext'
    })
  }

  async function asyncWrapper(fn: any, options?: { silent?: boolean }) {
    try {
      return await fn
    } catch (error: any) {
      handleError(error, { silent: options?.silent, context: { source: 'asyncWrapper' } })
    }
  }

  async function handleAsync(fn: () => Promise<any>, options?: { silent?: boolean }) {
    try {
      const result = await fn()
      setBackdropLoading(false)
      return result
    } catch (error: any) {
      handleError(error, { silent: options?.silent, context: { source: 'handleAsync' } })
      setBackdropLoading(false)
    }
  }

  const contextValue = useMemo(
    () => ({ handleError, asyncWrapper, openError, openSuccess, handleAsync }),
    [handleError, asyncWrapper, openError, openSuccess, handleAsync]
  )

  return <ErrorContext.Provider value={contextValue}>{children}</ErrorContext.Provider>
}

export default ErrorContextProvider
